import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { useGamePassSummary } from '@/components/dashboard/game-pass-card'
import { GamePassFundingChoice } from '@/components/game-pass-funding'
import { Button } from '@/components/ui/button'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'

type FundingSource = 'credits' | 'game-pass'

export function RoomGamePassPayment({
  roomId,
  policy,
  payment,
  onSaved,
  onPendingChange,
}: {
  roomId: string
  policy: 'split' | 'host'
  payment: { source: FundingSource, isMember: boolean }
  onSaved: () => Promise<unknown>
  onPendingChange: (pending: boolean) => void
}) {
  const trpc = useTRPC()
  const { t } = useI18n()
  const summary = useGamePassSummary()
  const [choice, setChoice] = useState<FundingSource | null>(null)
  const [error, setError] = useState('')
  const source = choice ?? (payment.isMember
    ? payment.source
    : (summary.data?.remaining ?? 0) > 0 ? 'game-pass' : 'credits')
  useEffect(() => {
    if (!payment.isMember && choice === null && (summary.data?.remaining ?? 0) > 0)
      setChoice('game-pass')
  }, [choice, payment.isMember, summary.data?.remaining])
  const update = useMutation(trpc.roomRouter.setFundingSource.mutationOptions())
  const join = useMutation(trpc.roomRouter.join.mutationOptions())
  const pending = update.isPending || join.isPending
  const unsaved = payment.isMember && choice !== null && !!error
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      onPendingChange(false)
    }
  }, [onPendingChange])

  function failureMessage(failure: unknown) {
    const key = failure instanceof Error ? failure.message : ''
    if (key === 'game_pass_unavailable' || key === 'game_pass_ineligible')
      return t(`gamePasses.errors.${key}`)
    const translated = t(`game.error.${key}`)
    return key && translated !== `game.error.${key}` ? translated : t('gamePasses.room.saveError')
  }

  async function change(next: FundingSource) {
    if (pending)
      return
    setError('')
    setChoice(next)
    if (!payment.isMember)
      return
    onPendingChange(true)
    try {
      const saved = await update.mutateAsync({ roomId, fundingSource: next })
      if (!mounted.current)
        return
      if (saved.source !== next)
        throw new Error('room_payment_not_confirmed')
      await onSaved()
      if (!mounted.current)
        return
      setChoice(null)
      onPendingChange(false)
    }
    catch (failure) {
      if (!mounted.current)
        return
      setError(failureMessage(failure))
      void summary.refetch()
    }
  }

  async function joinRoom() {
    if (pending)
      return
    setError('')
    onPendingChange(true)
    try {
      await join.mutateAsync({ roomId, fundingSource: source })
      if (!mounted.current)
        return
      await onSaved()
      if (!mounted.current)
        return
      setChoice(null)
    }
    catch (failure) {
      if (!mounted.current)
        return
      setError(failureMessage(failure))
      void summary.refetch()
    }
    finally {
      if (mounted.current)
        onPendingChange(false)
    }
  }

  return (
    <div className="space-y-3" aria-busy={pending}>
      <GamePassFundingChoice
        summary={summary}
        source={source}
        onChange={(next) => { void change(next) }}
        freeMode={false}
        excluded={null}
        roomPolicy={policy}
        disabled={pending}
      />
      {error && <p role="alert" data-testid="room-payment-error" className="text-sm text-destructive">{error}</p>}
      {unsaved && (
        <div className="space-y-2">
          <p data-testid="room-payment-unsaved" role="status" className="text-sm text-muted-foreground">{t('gamePasses.room.unsaved')}</p>
          <Button
            type="button"
            data-testid="room-payment-retry"
            variant="outline"
            disabled={pending}
            onClick={() => {
              if (choice)
                void change(choice)
            }}
          >{t('gamePasses.retry')}
          </Button>
        </div>
      )}
      {!payment.isMember && (
        <Button type="button" data-testid="join-room-with-funding" disabled={pending || (!summary.data && (choice === null || source === 'game-pass'))} onClick={() => { void joinRoom() }} className="min-h-11 w-full">
          {t(pending ? 'gamePasses.room.joining' : 'gamePasses.room.join')}
        </Button>
      )}
    </div>
  )
}
